import { Skeleton } from "antd";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableNoContent from "@/Components/common/TableNoContent";
import { routingConstants } from "@/Constants/routingConstants";
import {
  deSelectAllListings,
  fetchExpandedUpcomingShifts,
  fetchUpcomingShifts,
  setLoading,
  updateSelectedUpcomingShifts,
  updateUpComingShiftPagination,
  updateUpComingShiftsSorting,
} from "@/reducers/clientcandidatedetail";
import {
  expandButtonColumn,
  getActionByHtml,
  getColumnNormalHtml,
  totalDaysColumn,
} from "@/utilities/datatable/columnFunctions";
import { sortByDate, sortByTime } from "@/utilities/DataTableSorting";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";

import { ContractsShifts } from "../../candidates/Listing/common/ContractsShifts";
import ClientPayPopover from "../../dataTables/ClientPayPopover";

/**
 * React component for displaying upcoming shifts listing.
 *
 * @returns {JSX.Element} The upcoming shifts listing component
 */
const UpcomingShiftsListing = () => {
  const {
    candidateDetails,
    candidateUpComingShifts,
    isLoading,
    upComingShiftFilters,
    upComingShiftPagination,
    upcomingShiftView,
    toggleListingSelection,
    upcomingSorting,
  } = useSelector((state) => state.clientCandidateDetail);
  const { clientUserAllPermissions } = useSelector((state) => state.session);

  const dispatch = useDispatch();
  const handleFetchUpcomingShifts = () => {
    if (candidateDetails && candidateDetails.candidate_id) {
      dispatch(
        fetchUpcomingShifts({
          candidate_id: candidateDetails.candidate_id,
        }),
      );
    }
  };
  const firstDetailUpdate = useRef(true);
  useEffect(() => {
    if (firstDetailUpdate.current) {
      firstDetailUpdate.current = false;
      return;
    }
    handleFetchUpcomingShifts();
  }, [candidateDetails]);
  useEffect(() => {
    handleFetchUpcomingShifts();
  }, []);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      dispatch(setLoading(true));
      dispatch(
        updateUpComingShiftPagination({
          ...upComingShiftPagination,
          currentPage: 1,
          isLastPage: false,
        }),
      );
      dispatch(
        fetchUpcomingShifts({
          candidate_id: candidateDetails.candidate_id,
        }),
      );
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [upComingShiftFilters.search]);
  const columns = [
    {
      name: "Ref No",
      sortable: true,
      sortField: "contract_ref_no",
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "contract_ref_no"),
      selector: (row) =>
        !clientUserAllPermissions ||
        clientUserAllPermissions.is_allow_to_view_contract_detail === 1 ? (
          <>
            <Link
              to={routingConstants.contractDetail(row.route_contract_id)}
              className="text-[14px] font-medium leading-[normal] text-txtDark"
            >
              {row.contract_ref_no}
            </Link>
          </>
        ) : (
          <span className="cursor-default text-[14px] font-medium leading-[normal] text-txtDark">
            {row.contract_ref_no}
          </span>
        ),
      width: upcomingShiftView === "shifts" ? "12%" : "14%",
    },
    {
      name: "Actioned By",
      // sortable: true,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "booked_by_name"),
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getActionByHtml(
            row.booked_by_name,
            row.booked_at,
            row.booked_from,
            "",
          )}
        </div>
      ),
      width: "18.3%",
    },
    ...(upcomingShiftView === "shifts"
      ? [
          {
            name: "Date",
            sortable: true,
            sortField: "contract_date",
            // sortFunction: (rowA, rowB) =>
            //   sortByDate(
            //     moment(rowA.shift_date).format("DD/MM/YYYY"),
            //     moment(rowB.shift_date).format("DD/MM/YYYY"),
            //   ),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                <>
                  {getColumnNormalHtml(
                    moment(row.shift_date).format("DD/MM/YYYY"),
                  )}
                </>
              </div>
            ),
            width: "11%",
          },
          {
            name: "Start Time",
            sortable: true,
            sortField: "contract_start_time",

            // sortFunction: (rowA, rowB) =>
            //   sortByTime(
            //     moment(rowA.contract_start_time, "HH:mm:ss").format("HH:mm"),
            //     moment(rowB.contract_start_time, "HH:mm:ss").format("HH:mm"),
            //   ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.contract_start_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "8%",
          },
          {
            name: "End Time",
            sortable: true,
            sortField: "contract_end_time",

            sortFunction: (rowA, rowB) =>
              sortByTime(
                moment(rowA.contract_end_time, "HH:mm:ss").format("HH:mm"),
                moment(rowB.contract_end_time, "HH:mm:ss").format("HH:mm"),
              ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.contract_end_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "8%",
          },
          {
            name: "Break",
            sortable: true,
            sortField: "un_paid_break_hour",

            sortFunction: (rowA, rowB) =>
              sortByTime(
                moment(rowA.break_time, "HH:mm:ss").format("HH:mm"),
                moment(rowB.break_time, "HH:mm:ss").format("HH:mm"),
              ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.break_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "7%",
          },

          {
            name: "Admin",
            sortable: true,
            sortField: "contract_admin_time",
            // sortFunction: (rowA, rowB) =>
            //   sortByTime(
            //     moment(rowA.admin_time, "HH:mm:ss").format("HH:mm"),
            //     moment(rowB.admin_time, "HH:mm:ss").format("HH:mm"),
            //   ),
            selector: (row) =>
              getColumnNormalHtml(
                moment(row.admin_time, "HH:mm:ss").format("HH:mm"),
              ),
            width: "7.4%",
          },
          {
            name: "Visit",
            sortable: true,
            sortField: "contract_no_of_visit",
            // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "no_of_visit"),
            selector: (row) =>
              getColumnNormalHtml(row.no_of_visit ? row.no_of_visit : 0),
            width: "5%",
          },
          {
            name: "Unit",
            selector: (row) => (
              <div className="flex flex-col items-end">
                {row.contract_duration !== "-"
                  ? getColumnNormalHtml(
                      Number(`${row.contract_duration}`).toFixed(2),
                    )
                  : "0.00"}
              </div>
            ),
            width: "7%",
          },
          {
            name: "Total",

            selector: (row) => (
              <ClientPayPopover
                pay_arr={row?.client_pay_arr}
                shift_rate={
                  Number(row.rate) +
                  Number(row.cover_request_awarded_reward || 0) -
                  Number(row.cover_request_awarded_penalty || 0)
                }
                is_substitute_contract={row?.is_substitute_contract}
                cover_request_awarded_penalty={
                  row?.cover_request_awarded_penalty
                }
                cover_request_awarded_reward={row.cover_request_awarded_reward}
                content={(content) => (
                  <div
                    className="flex flex-col gap-1 break-keep"
                    data-tag="allowRowEvents"
                  >
                    {getColumnNormalHtml(content, "cursor-pointer")}
                  </div>
                )}
              />
            ),
          },
        ]
      : [
          {
            name: "Start Date",
            sortable: true,
            sortFunction: (rowA, rowB) =>
              sortByDate(
                moment(rowA.contract_start_date).format("DD/MM/YYYY"),
                moment(rowB.contract_start_date).format("DD/MM/YYYY"),
              ),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                {getColumnNormalHtml(
                  moment(row.contract_start_date).format("DD/MM/YYYY"),
                )}
              </div>
            ),
            width: "",
          },
          {
            name: "End Date",
            sortable: true,
            sortFunction: (rowA, rowB) =>
              sortByDate(
                moment(rowA.contract_end_date).format("DD/MM/YYYY"),
                moment(rowB.contract_end_date).format("DD/MM/YYYY"),
              ),
            selector: (row) => (
              <div
                className="flex flex-col items-end gap-1"
                data-tag="allowRowEvents"
              >
                {getColumnNormalHtml(
                  moment(row.contract_end_date).format("DD/MM/YYYY"),
                )}
              </div>
            ),
            width: "",
          },
          totalDaysColumn("Total Days", "days_total", "11.5%"),
          totalDaysColumn("Booked", "days_booked", "10%"),
          totalDaysColumn("Worked", "days_worked", "10%"),
          expandButtonColumn(),
        ]),
  ];
  const wrapper = useRef(null);
  let height = wrapper?.current?.clientHeight
    ? wrapper?.current?.clientHeight - 66
    : 400;
  const dataTableHeight = `${height}px`;
  const dataTableRef = useRef(null);
  /**
   * Handles the table scroll event.
   *
   * @param {object} e - The event object
   * @return {void}
   */
  // const handleTableScroll = (e) => {
  //   let element = e.target;
  //   let scrollHeight = element.scrollHeight;
  //   let scrollTop = element.scrollTop;
  //   let clientHeight = element.clientHeight;

  //   if (scrollHeight - scrollTop - clientHeight < 2) {
  //     dispatch(
  //       fetchUpcomingShifts({
  //         candidate_id: candidateDetails.candidate_id,
  //       }),
  //     );
  //   }
  // };

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(
      fetchUpcomingShifts({
        candidate_id: candidateDetails.candidate_id,
      }),
    ),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const Expandedrow = ({ data }) => (
    <ContractsShifts
      shifts={data.shifts}
      dateFormat="YYYY-MM-DD"
      substitueCon={data?.is_substitute_contract}
    />
  );

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = upcomingSorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateUpComingShiftsSorting({
        sortByColumn: newSortField,
        sortByOrder: newSortOrder,
      }),
    );
    dispatch(deSelectAllListings());
    dispatch(
      updateUpComingShiftPagination({
        ...upComingShiftPagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setLoading(true));
    dispatch(fetchUpcomingShifts());
  };

  return (
    <div className="UpcomingShiftsListing_Wrapper flex-1" ref={wrapper}>
      <div className="DataTable_Wrapper hide_extended_arrow" ref={dataTableRef}>
        <DataTable
          columns={columns}
          data={candidateUpComingShifts}
          fixedHeader
          onSort={handleSort}
          // persistTableHead={true}
          clearSelectedRows={toggleListingSelection}
          selectableRows
          onSelectedRowsChange={(state) => {
            let selectedContracts = state.selectedRows.map((item) => {
              return item.contract_id;
            });
            dispatch(updateSelectedUpcomingShifts(selectedContracts));
          }}
          noDataComponent={<TableNoContent />}
          progressPending={isLoading}
          progressComponent={<Skeleton active paragraph={{ rows: 1 }} />}
          className="_DataTable h-[calc(100vh-40vh)] !min-h-[522px]"
          fixedHeaderScrollHeight={"730px"}
          expandableRows={upcomingShiftView !== "shifts"}
          expandOnRowClicked
          expandableRowsComponent={Expandedrow}
          onRowExpandToggled={(state, row) => {
            if (state) {
              dispatch(
                fetchExpandedUpcomingShifts({
                  contractId: row.contract_id,
                  //     candidate_id: row.candidate_id,
                }),
              );
            }
          }}
        />
      </div>
    </div>
  );
};

export default UpcomingShiftsListing;
